/**
 * SellerTourCalendar Component - Seller-facing tour management dashboard.
 * Shows a weekly calendar view, pending requests, and upcoming scheduled tours.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import APIAgent from '../../agents/apiAgent';
import SellerAvailabilitySettings from './SellerAvailabilitySettings';
import { TourActionModal, ScheduleTourModal } from './modals';
import tourErrorMessage from './tourErrorMessage';
import './SellerTourCalendar.css';
import { hasTourStarted, isPastAtProperty, tourStatusLabel, TOUR_NOT_STARTED_TEXT } from './tourTime';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** Returns the Monday of the week containing `date`. */
const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Parse a backend datetime string as local wall-clock time. Backend stores
 * naive datetimes; strip any timezone marker so JS does not shift the value.
 */
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const normalized = String(dateStr).replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, '');
  return new Date(normalized);
};

const formatDateTime = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return 'Not scheduled';
  return d.toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatTime = (dateStr) => {
  const d = parseDate(dateStr);
  if (!d) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Label for the seller's set-a-time button. A pending request with buyer
 * suggestions has nothing to "reschedule" yet; only a booked tour does.
 */
const scheduleActionLabel = (tour) => {
  if (tour.status === 'scheduled') return 'Reschedule…';
  if (Array.isArray(tour.preferred_dates_json) && tour.preferred_dates_json.length > 0) {
    return 'Propose another time…';
  }
  return 'Set time…';
};

const StatusBadge = ({ status }) => (
  <span className={`status-badge status-${status}`}>{tourStatusLabel(status)}</span>
);

/**
 * One tour request / scheduled tour. Defined at module level so the card (and
 * the button that opened a dialog) survives parent re-renders; otherwise focus
 * can't return to that button when the dialog closes.
 */
const TourCard = ({ tour, house, showScheduleAction = false, onAction }) => {
  return (
    <div className="agent-tour-card">
      <div className="agent-tour-card-body">
        <div className="agent-tour-card-header">
          <h4>{house?.address || `Property #${tour.house_id}`}</h4>
          <StatusBadge status={tour.status} />
        </div>

        {house && (
          <div className="tour-card-location">
            {house.city}, {house.state} {house.zip_code}
          </div>
        )}

        <div className="tour-meta">
          <div className="tour-meta-row">
            <span className="meta-label">Buyer</span>
            <span className="meta-value">
              {tour.buyer_name || `Buyer #${tour.buyer_id}`}
            </span>
          </div>
          {tour.buyer_email && (
            <div className="tour-meta-row">
              <span className="meta-label">Email</span>
              <span className="meta-value">
                <a href={`mailto:${tour.buyer_email}`}>{tour.buyer_email}</a>
              </span>
            </div>
          )}
          {tour.scheduled_date && (
            <div className="tour-meta-row">
              <span className="meta-label">Scheduled</span>
              <span className="meta-value">{formatDateTime(tour.scheduled_date)}</span>
            </div>
          )}
          {tour.scheduled_duration_minutes && (
            <div className="tour-meta-row">
              <span className="meta-label">Duration</span>
              <span className="meta-value">{tour.scheduled_duration_minutes} min</span>
            </div>
          )}
          <div className="tour-meta-row">
            <span className="meta-label">Requested</span>
            <span className="meta-value">
              {new Date(tour.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        {tour.message && (
          <div className="tour-message-box">
            <strong>Buyer's message</strong>
            {tour.message}
          </div>
        )}

        {/* Buyer-suggested times: approve (and schedule) at any preferred slot. */}
        {(tour.status === 'pending' || tour.status === 'seller_approved') &&
          Array.isArray(tour.preferred_dates_json) &&
          tour.preferred_dates_json.length > 0 && (
            <div className="preferred-times-box">
              <strong>Buyer's preferred time{tour.preferred_dates_json.length > 1 ? 's' : ''}</strong>
              <div className="preferred-times-list">
                {tour.preferred_dates_json.map((slot, idx) => (
                  <div key={idx} className="preferred-time-row">
                    <span className="preferred-time-label">{formatDateTime(slot)}</span>
                    {isPastAtProperty(slot, (house || tour.house)?.timezone) ? (
                      <span className="meta-label">Time has passed</span>
                    ) : (
                      <button
                        type="button"
                        className="btn-schedule btn-approve-time"
                        onClick={() => onAction('approve', tour, { slot })}
                        title="Approve and schedule at this time"
                      >
                        ✓ Approve this time
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        <div className="card-actions">
          {(tour.status === 'pending' || tour.status === 'seller_approved') && (
            <button
              className="btn-cancel"
              onClick={() => onAction('decline', tour)}
            >
              Decline
            </button>
          )}
          {showScheduleAction && !hasTourStarted(tour) && (
            <button
              className="btn-secondary"
              onClick={() => onAction('schedule', tour)}
            >
              {scheduleActionLabel(tour)}
            </button>
          )}
          {tour.status === 'scheduled' && (
            <button
              className="btn-complete"
              onClick={() => onAction('complete', tour)}
              disabled={!hasTourStarted(tour)}
              title={hasTourStarted(tour) ? undefined : TOUR_NOT_STARTED_TEXT}
            >
              Mark Complete
            </button>
          )}
          {(tour.status === 'pending'
            || tour.status === 'scheduled'
            || tour.status === 'seller_approved') && (
            <button
              className="btn-cancel"
              onClick={() => onAction('cancel', tour)}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SellerTourCalendar = ({ userId }) => {
  // Every tour on THIS seller's listings. Scheduled tours come from here too,
  // not /upcoming: that endpoint also returns tours where the user is the
  // buyer, and drops a tour once it starts, which left the seller no way to
  // mark it complete.
  const [sellerTours, setSellerTours] = useState([]);
  const [toursLoading, setToursLoading] = useState(false);
  const [toursError, setToursError] = useState(null);
  const [houses, setHouses] = useState({});
  // The action dialog currently open:
  // { type: 'schedule' | 'approve' | 'decline' | 'complete' | 'cancel', tour, slot? }
  const [action, setAction] = useState(null);
  // The button that opened the current dialog, and where focus goes when a
  // successful action removes it (e.g. an approved tour moves to Upcoming).
  const actionTriggerRef = useRef(null);
  const headingRef = useRef(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [weekStart, setWeekStart] = useState(() => getWeekStart());
  const [activeTab, setActiveTab] = useState('tours'); // 'tours' | 'availability'

  /** Fetch every tour request on THIS seller's properties. */
  const fetchSellerTours = useCallback(async () => {
    if (!userId) return;
    try {
      setToursLoading(true);
      setToursError(null);
      const data = await APIAgent.request(
        `/api/tour-requests?seller_id=${userId}`,
        'GET'
      );
      setSellerTours(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching seller tours:', err);
      setToursError(err.message);
    } finally {
      setToursLoading(false);
    }
  }, [userId]);

  // Pending requests and seller-approved requests still awaiting a time.
  const pendingTours = useMemo(
    () => sellerTours.filter((t) => t.status === 'pending' || t.status === 'seller_approved'),
    [sellerTours]
  );
  // Booked tours, soonest first — including ones under way or just finished,
  // which stay here until marked complete or cancelled.
  const upcomingTours = useMemo(
    () =>
      sellerTours
        .filter((t) => t.status === 'scheduled' && t.scheduled_date)
        .sort((a, b) => parseDate(a.scheduled_date) - parseDate(b.scheduled_date)),
    [sellerTours]
  );

  // House ids already fetched or in flight, so a list refresh only fetches
  // houses it hasn't seen (a failed fetch is dropped so it can retry).
  const requestedHouseIds = useRef(new Set());

  /** Build a house lookup map by fetching only the houses we need by ID. */
  const loadHouses = useCallback(async (tourLists) => {
    try {
      const allTours = tourLists.flat().filter(Boolean);
      const ids = [...new Set(allTours.map((t) => t.house_id).filter(Boolean))].filter(
        (id) => !requestedHouseIds.current.has(id)
      );
      if (ids.length === 0) return;
      ids.forEach((id) => requestedHouseIds.current.add(id));
      const results = await Promise.all(
        ids.map((id) =>
          APIAgent.request(`/api/houses/${id}`, 'GET').catch(() => {
            requestedHouseIds.current.delete(id);
            return null;
          })
        )
      );
      setHouses((prev) => {
        const next = { ...prev };
        results.filter(Boolean).forEach((h) => { next[h.id] = h; });
        return next;
      });
    } catch (err) {
      console.error('Error fetching houses:', err);
    }
  }, []);

  useEffect(() => {
    fetchSellerTours();
  }, [fetchSellerTours]);

  // Load house details whenever the tour lists change
  useEffect(() => {
    loadHouses([upcomingTours, pendingTours]);
  }, [upcomingTours, pendingTours, loadHouses]);

  const refresh = fetchSellerTours;

  const openAction = (type, tour, extra = {}) => {
    actionTriggerRef.current = document.activeElement;
    setActionError(null);
    setAction({ type, tour, ...extra });
  };

  const closeAction = () => {
    setAction(null);
    setActionError(null);
  };

  /**
   * Run the open dialog's request. On success close it; on failure keep it
   * open with the error shown in context. Refresh either way so the cards
   * behind the dialog show the tour's current server state.
   */
  const runAction = async (request, fallbackError) => {
    setActionBusy(true);
    setActionError(null);
    try {
      await request();
      setAction(null);
      await refresh();
      // The dialog hands focus back to its trigger, but the refresh may have
      // removed that card. Once the re-render lands, keep focus on the page.
      window.setTimeout(() => {
        const trigger = actionTriggerRef.current;
        if (!trigger || !trigger.isConnected) headingRef.current?.focus();
      }, 0);
    } catch (err) {
      setActionError(tourErrorMessage(err, fallbackError));
      // The tour may have changed server-side (e.g. the buyer cancelled);
      // show its current state behind the dialog.
      await refresh();
    } finally {
      setActionBusy(false);
    }
  };

  const handleScheduleConfirm = (tourId, date, time, duration, timeZone) => {
    if (!date || !time) {
      setActionError('Please select a date and time.');
      return;
    }
    if (isPastAtProperty(`${date}T${time}:00`, timeZone)) {
      setActionError('That time has already passed. Pick a future time.');
      return;
    }
    // Send naive local wall-clock ISO; backend stores it as-is so the
    // displayed time always matches what the seller chose.
    runAction(
      () =>
        APIAgent.request(`/api/tour-requests/${tourId}/schedule`, 'PUT', {
          scheduled_date: `${date}T${time}:00`,
          scheduled_duration_minutes: duration,
        }),
      'Error scheduling tour. Please try again.'
    );
  };

  const handleCancel = (tourId, reason) =>
    runAction(
      () =>
        APIAgent.request(
          reason
            ? `/api/tour-requests/${tourId}/cancel?reason=${encodeURIComponent(reason)}`
            : `/api/tour-requests/${tourId}/cancel`,
          'PUT'
        ),
      'Error cancelling tour. Please try again.'
    );

  /**
   * Approve a tour AND book it at one of the buyer's suggested times in a
   * single request, so a failure can't leave it approved with no time.
   * `isoSlot` is the buyer's preferred datetime string (naive local).
   */
  const handleApproveAtBuyerTime = (tourId, isoSlot, duration = 30) =>
    runAction(
      () =>
        APIAgent.request(`/api/tour-requests/${tourId}/seller-approve`, 'POST', {
          // Strip any TZ marker so the wall-clock value is preserved end-to-end.
          scheduled_date: String(isoSlot).replace(/Z$/, '').replace(/[+-]\d{2}:\d{2}$/, ''),
          scheduled_duration_minutes: duration,
        }),
      'Error approving the buyer’s time. Please try again.'
    );

  const handleDecline = (tourId, reason) =>
    runAction(
      () =>
        APIAgent.request(`/api/tour-requests/${tourId}/seller-decline`, 'POST', {
          reason: reason || undefined,
        }),
      'Error declining tour. Please try again.'
    );

  const handleComplete = (tourId) =>
    runAction(
      () => APIAgent.request(`/api/tour-requests/${tourId}/complete`, 'POST'),
      'Error completing tour. Please try again.'
    );

  // ── Weekly calendar ────────────────────────────────────────

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  const toursForDay = (day) =>
    upcomingTours.filter((t) => {
      const d = parseDate(t.scheduled_date);
      return d && d.toDateString() === day.toDateString();
    });

  const weekLabel = `${MONTH_NAMES[weekDays[0].getMonth()]} ${weekDays[0].getDate()} – ${MONTH_NAMES[weekDays[6].getMonth()]} ${weekDays[6].getDate()}, ${weekDays[6].getFullYear()}`;

  const today = new Date();

  const shiftWeek = (delta) =>
    setWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + delta * 7);
      return d;
    });

  // ── Action dialogs ─────────────────────────────────────────

  const renderActionModal = () => {
    if (!action) return null;
    const { type, tour, slot } = action;
    const duration = tour.scheduled_duration_minutes || 30;
    const details = [
      { label: 'Property', value: houses[tour.house_id]?.address || `Property #${tour.house_id}` },
      { label: 'Buyer', value: tour.buyer_name || `Buyer #${tour.buyer_id}` },
    ];
    if (type !== 'approve' && tour.scheduled_date) {
      details.push({ label: 'Scheduled', value: formatDateTime(tour.scheduled_date) });
    }
    const shared = {
      open: true,
      details,
      submitting: actionBusy,
      error: actionError,
      onClose: closeAction,
    };

    switch (type) {
      case 'schedule':
        return (
          <ScheduleTourModal
            {...shared}
            title={tour.status === 'scheduled' ? 'Reschedule tour' : 'Schedule tour'}
            // Keep the length the buyer asked for instead of resetting to 30.
            initialDuration={duration}
            onConfirm={(date, time, dur) => handleScheduleConfirm(
              tour.id, date, time, dur, (houses[tour.house_id] || tour.house)?.timezone
            )}
          />
        );
      case 'approve':
        return (
          <TourActionModal
            {...shared}
            details={[
              ...details,
              { label: 'Time', value: `${formatDateTime(slot)} (${duration} min)` },
            ]}
            title="Approve tour time"
            prompt="Approve this request and schedule the tour at the buyer's suggested time? The buyer will be notified."
            confirmLabel="Approve & schedule"
            busyLabel="Scheduling…"
            onConfirm={() =>
              // Keep the length the buyer asked for; passing a literal 30
              // here overwrote a 60-minute request.
              handleApproveAtBuyerTime(tour.id, slot, duration)
            }
          />
        );
      case 'decline':
        return (
          <TourActionModal
            {...shared}
            title="Decline tour request"
            prompt="Decline this tour request? The buyer will be notified."
            reasonLabel="Reason (optional, shared with the buyer)"
            reasonPlaceholder="e.g., Property is no longer available for showings"
            confirmLabel="Decline request"
            busyLabel="Declining…"
            tone="danger"
            onConfirm={(reason) => handleDecline(tour.id, reason)}
          />
        );
      case 'complete':
        return (
          <TourActionModal
            {...shared}
            title="Mark tour complete"
            prompt="Mark this tour as completed?"
            confirmLabel="Mark complete"
            busyLabel="Saving…"
            onConfirm={() => handleComplete(tour.id)}
          />
        );
      case 'cancel':
        return (
          <TourActionModal
            {...shared}
            title="Cancel tour"
            prompt={
              tour.status === 'scheduled'
                ? 'Cancel this scheduled tour? The buyer will be notified.'
                : 'Cancel this tour request? The buyer will be notified.'
            }
            reasonLabel="Reason (optional, shared with the buyer)"
            reasonPlaceholder="e.g., Schedule conflict"
            confirmLabel="Cancel tour"
            busyLabel="Cancelling…"
            dismissLabel="Keep tour"
            tone="danger"
            onConfirm={(reason) => handleCancel(tour.id, reason)}
          />
        );
      default:
        return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────

  if (toursLoading && !sellerTours.length) {
    return <div className="agent-tour-calendar calendar-loading">Loading tours…</div>;
  }

  return (
    <div className="agent-tour-calendar">
      <div className="calendar-page-header">
        <h2 ref={headingRef} tabIndex={-1}>My Tour Calendar</h2>
        <span className="section-badge">
          {upcomingTours.length + pendingTours.length} total
        </span>
      </div>

      {/* Tab bar */}
      <div className="sac-tabs">
        <button
          className={`sac-tab${activeTab === 'tours' ? ' active' : ''}`}
          onClick={() => setActiveTab('tours')}
        >
          🗓 Tours
        </button>
        <button
          className={`sac-tab${activeTab === 'availability' ? ' active' : ''}`}
          onClick={() => setActiveTab('availability')}
        >
          ⏰ Availability
        </button>
      </div>

      {activeTab === 'tours' && (
        <>
      {/* ── Weekly Calendar ────────────────────────────────── */}
      <div className="calendar-section">
        <div className="section-header">
          <h3>📅 Weekly View</h3>
        </div>
        <div className="week-nav">
          <button className="week-nav-btn" onClick={() => shiftWeek(-1)} aria-label="Previous week">
            ‹
          </button>
          <span className="week-label">{weekLabel}</span>
          <button className="week-nav-btn" onClick={() => shiftWeek(1)} aria-label="Next week">
            ›
          </button>
        </div>
        <div className="weekly-grid">
          {weekDays.map((day, i) => {
            const dayTours = toursForDay(day);
            const isToday = day.toDateString() === today.toDateString();
            return (
              <div key={i} className={`day-column${isToday ? ' today' : ''}`}>
                <div className="day-column-header">
                  <div>{DAY_NAMES[i]}</div>
                  <div>{day.getDate()}</div>
                </div>
                <div className="day-column-body">
                  {dayTours.length === 0 ? (
                    <span className="day-column-empty">—</span>
                  ) : (
                    dayTours.map((t) => (
                      <div
                        key={t.id}
                        className="calendar-tour-chip"
                        title={houses[t.house_id]?.address}
                      >
                        <span className="chip-time">{formatTime(t.scheduled_date)}</span>
                        <span className="chip-address">
                          {houses[t.house_id]?.address || `#${t.house_id}`}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Pending Requests ───────────────────────────────── */}
      <div className="calendar-section">
        <div className="section-header">
          <h3>⏳ Pending Requests</h3>
          {pendingTours.length > 0 && (
            <span
              className="section-badge"
              style={{ background: '#856404', color: '#fff3cd' }}
            >
              {pendingTours.length}
            </span>
          )}
        </div>
        {toursError && <div className="error-message">{toursError}</div>}
        {toursLoading && pendingTours.length === 0 ? (
          <div className="calendar-loading">Loading…</div>
        ) : pendingTours.length === 0 ? (
          <div className="empty-section">No pending tour requests.</div>
        ) : (
          <div className="tours-grid">
            {pendingTours.map((tour) => (
              <TourCard
                key={tour.id}
                tour={tour}
                house={houses[tour.house_id]}
                showScheduleAction
                onAction={openAction}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Upcoming Tours ─────────────────────────────────── */}
      <div className="calendar-section">
        <div className="section-header">
          <h3>🗓 Scheduled Tours</h3>
          {upcomingTours.length > 0 && (
            <span className="section-badge">{upcomingTours.length}</span>
          )}
        </div>
        {toursLoading && upcomingTours.length === 0 ? (
          <div className="calendar-loading">Loading…</div>
        ) : upcomingTours.length === 0 ? (
          <div className="empty-section">No tours scheduled.</div>
        ) : (
          <div className="tours-grid">
            {upcomingTours.map((tour) => (
              <TourCard
                key={tour.id}
                tour={tour}
                house={houses[tour.house_id]}
                showScheduleAction
                onAction={openAction}
              />
            ))}
          </div>
        )}
      </div>
        </>
      )}

      {activeTab === 'availability' && (
        <SellerAvailabilitySettings sellerId={userId} />
      )}

      {renderActionModal()}
    </div>
  );
};

export default SellerTourCalendar;
